import { mapInfo, MAP_IDS } from "@69ly/maps";
import { cmPer360, edpi, type Leaderboard, type LeaderboardMember, type LeetifyBoard, type StatSource } from "@69ly/shared";
import { Link } from "react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { LeetifyCredit, ViewOnLeetify } from "../components/LeetifyCredit.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useHideMe, useLeaderboard, useLeetify, useRefreshStats } from "../lib/leaderboard-queries.ts";

type Period = "lifetime" | "recent";

/** One column of a source: its label, how to read it for a member, and how to print it. */
interface Column {
  key: string;
  label: string;
  value: (id: string) => number | null;
  format?: (n: number) => string;
  /** Lower is better (none yet, but kept for clarity of sorting). */
  low?: boolean;
  /** No better or worse: no leader marked (eDPI, DPI). */
  neutral?: boolean;
}

/** Up to three decimals, no trailing zeros: 1.25, 0.8, 2. */
const plain = (n: number) => String(Math.round(n * 1000) / 1000);

function columnsFor(source: StatSource, period: Period, board: Leaderboard, leet: LeetifyBoard | undefined, t: (k: string) => string): Column[] {
  const pct = (n: number) => `${Math.round(n)} %`;
  if (source === "faceit") {
    const f = (id: string) => board.faceit.stats[id]?.data;
    return [
      { key: "elo", label: t("leaderboard.col.elo"), value: (id) => f(id)?.elo ?? null },
      { key: "level", label: t("leaderboard.col.level"), value: (id) => f(id)?.level ?? null },
      { key: "kd", label: t("leaderboard.col.kd"), value: (id) => f(id)?.[period].kd ?? null, format: (n) => n.toFixed(2) },
      { key: "hs", label: t("leaderboard.col.hs"), value: (id) => f(id)?.[period].hs ?? null, format: pct },
      { key: "win", label: t("leaderboard.col.winRate"), value: (id) => f(id)?.[period].winRate ?? null, format: pct },
      { key: "adr", label: t("leaderboard.col.adr"), value: (id) => f(id)?.[period].adr ?? null, format: (n) => n.toFixed(1) },
      { key: "matches", label: t("leaderboard.col.matches"), value: (id) => f(id)?.[period].matches ?? null },
    ];
  }
  if (source === "leetify") {
    // Leetify's own names, and its numbers exactly as its API gives them: no
    // rounding, signs or units added (Leetify's developer guidelines).
    const l = (id: string) => leet?.stats[id]?.data;
    return [
      { key: "rating", label: "Leetify Rating", value: (id) => l(id)?.leetifyRating ?? null },
      { key: "aim", label: "Aim", value: (id) => l(id)?.aim ?? null },
      { key: "positioning", label: "Positioning", value: (id) => l(id)?.positioning ?? null },
      { key: "utility", label: "Utility", value: (id) => l(id)?.utility ?? null },
      { key: "premier", label: "Premier", value: (id) => l(id)?.premier ?? null },
      { key: "matches", label: t("leaderboard.col.matches"), value: (id) => l(id)?.totalMatches ?? null },
    ];
  }
  if (source === "published") {
    const p = (id: string) => board.published[id];
    // A column for each map someone published on, in the map pool's order.
    const maps = MAP_IDS.filter((m) => Object.values(board.published).some((x) => (x.byMap[m] ?? 0) > 0));
    return [
      { key: "total", label: t("leaderboard.col.total"), value: (id) => p(id)?.total ?? null },
      { key: "singles", label: t("leaderboard.col.singles"), value: (id) => p(id)?.singles ?? null },
      { key: "combos", label: t("leaderboard.col.combos"), value: (id) => p(id)?.combos ?? null },
      { key: "videos", label: t("leaderboard.col.videos"), value: (id) => p(id)?.videos ?? null },
      { key: "pictures", label: t("leaderboard.col.pictures"), value: (id) => p(id)?.pictures ?? null },
      ...maps.map((m): Column => ({ key: m, label: mapInfo(m).name, value: (id) => (p(id) ? (p(id)!.byMap[m] ?? 0) : null) })),
    ];
  }
  if (source === "aim") {
    const a = (id: string) => board.aim[id];
    return [
      { key: "edpi", label: t("leaderboard.col.edpi"), value: (id) => edpi(a(id)), format: plain, neutral: true },
      { key: "dpi", label: t("leaderboard.col.dpi"), value: (id) => a(id)?.dpi ?? null, neutral: true },
      { key: "sens", label: t("leaderboard.col.sens"), value: (id) => a(id)?.sensitivity ?? null, format: plain, neutral: true },
      { key: "cm", label: t("leaderboard.col.cm360"), value: (id) => cmPer360(edpi(a(id))), format: (n) => `${n.toFixed(1)} cm`, neutral: true },
    ];
  }
  const p = (id: string) => board.playbook[id];
  return [
    { key: "share", label: t("leaderboard.col.share"), value: (id) => p(id)?.share ?? null, format: pct },
    { key: "learned", label: t("leaderboard.col.learned"), value: (id) => p(id)?.learned ?? null },
    { key: "practising", label: t("leaderboard.col.practising"), value: (id) => p(id)?.practising ?? null },
    { key: "tracked", label: t("leaderboard.col.tracked"), value: (id) => p(id)?.tracked ?? null },
    { key: "ready", label: t("leaderboard.col.ready"), value: (id) => p(id)?.executionsReady ?? null },
  ];
}

function ago(iso: string | null, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!iso) return t("leaderboard.never");
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (min < 1) return t("leaderboard.justNow");
  if (min < 60) return t("leaderboard.minutesAgo", { count: min });
  return t("leaderboard.hoursAgo", { count: Math.round(min / 60) });
}

/** The team's numbers side by side, one source at a time, with a podium for the column it is sorted by. */
export function LeaderboardPage() {
  const { t } = useTranslation();
  const { scope, team } = useCurrentTeam();
  const board = useLeaderboard(scope);
  const refresh = useRefreshStats(scope);
  const hide = useHideMe(scope);
  const [source, setSource] = useState<StatSource>("playbook");
  const [period, setPeriod] = useState<Period>("lifetime");
  const [sort, setSort] = useState<{ key: string | null; dir: "desc" | "asc" }>({ key: null, dir: "desc" });
  // The box follows the click at once, whatever an in-flight refresh of the board says.
  const [hiddenNow, setHiddenNow] = useState<boolean | null>(null);
  // Leetify is asked live, only while its tab is open: nothing of it is kept.
  const leetify = useLeetify(scope, source === "leetify");

  if (board.isPending) return <Loading />;
  if (board.error) return <ErrorState message={errorText(t, board.error)} onRetry={() => void board.refetch()} />;
  const data = board.data;
  const cols = columnsFor(source, period, data, leetify.data, t);
  const isLeetify = source === "leetify";
  const leetifyFailed = isLeetify && !!leetify.data && data.members.length > 0 && data.members.every((m) => leetify.data.stats[m.id]?.failed);
  const col = cols.find((c) => c.key === sort.key) ?? cols[0];
  const sorted = [...data.members].sort((a, b) => {
    const va = col.value(a.id);
    const vb = col.value(b.id);
    if (va === null && vb === null) return a.name.localeCompare(b.name);
    if (va === null) return 1;
    if (vb === null) return -1;
    return sort.dir === "desc" ? vb - va : va - vb;
  });
  // Nobody has published yet: zeros make no podium.
  const withValue = sorted.filter((m) => col.value(m.id) !== null && !(source === "published" && col.value(m.id) === 0));
  const podium = withValue.slice(0, 3);
  const others = sorted.filter((m) => !podium.includes(m));
  // The leader of each column, when there is one (not when everyone has the same number).
  const best = Object.fromEntries(
    cols.map((c) => {
      const vs = data.members.map((m) => c.value(m.id)).filter((v): v is number => v !== null);
      const max = Math.max(...vs);
      return [c.key, !c.neutral && vs.length > 1 && vs.some((v) => v !== max) ? max : null];
    }),
  );
  const fmt = (c: Column, v: number | null) => (v === null ? "–" : c.format ? c.format(v) : String(v));
  const me = data.members.find((m) => m.me);
  const profile = (m: LeaderboardMember) => (source === "faceit" ? data.faceit.stats[m.id]?.url : null);
  const leetifyUrl = (m: LeaderboardMember) => (isLeetify ? leetify.data?.stats[m.id]?.url : null);

  return (
    <div className="page leaderboard">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t("leaderboard.title")}</h1>
          <p className="muted">{team ? t("leaderboard.intro", { team: team.name }) : t("leaderboard.personal")}</p>
        </div>
        <div className="lb-refresh">
          <span className="muted small" aria-live="polite">
            {isLeetify
              ? leetify.isFetching
                ? t("leaderboard.askingLeetify")
                : t("leaderboard.leetifyLive")
              : data.refreshing
                ? t("leaderboard.refreshing")
                : t("leaderboard.updated", { when: ago(data.refreshedAt, t) })}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            disabled={isLeetify ? leetify.isFetching : refresh.isPending || data.refreshing}
            onClick={() => (isLeetify ? void leetify.refetch() : refresh.mutate())}
          >
            <Icon name="loop" size={14} />
            {t("leaderboard.refresh")}
          </button>
        </div>
      </div>
      {refresh.error && !isLeetify && <p className="field-error small">{errorText(t, refresh.error)}</p>}

      <div className="lb-bar">
        <div className="segmented" role="group" aria-label={t("leaderboard.source")}>
          {(["playbook", "published", "aim", "faceit", "leetify"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className="seg"
              aria-pressed={source === s}
              onClick={() => {
                setSource(s);
                setSort({ key: null, dir: "desc" });
              }}
            >
              {t(`leaderboard.sources.${s}`)}
            </button>
          ))}
        </div>
        {source === "faceit" && (
          <div className="segmented" role="group" aria-label={t("leaderboard.period")}>
            {(["lifetime", "recent"] as const).map((p) => (
              <button key={p} type="button" className="seg" aria-pressed={period === p} onClick={() => setPeriod(p)}>
                {t(`leaderboard.periods.${p}`)}
              </button>
            ))}
          </div>
        )}
        {team && me && (
          <label className="check small lb-hide">
            <input
              type="checkbox"
              checked={hiddenNow ?? me.hidden}
              onChange={(e) => {
                setHiddenNow(e.target.checked);
                hide.mutate(e.target.checked, { onSettled: () => setHiddenNow(null) });
              }}
            />
            {t("leaderboard.hideMe")}
          </label>
        )}
      </div>

      {source === "faceit" && !data.faceit.configured && <p className="banner banner-info">{t("leaderboard.noFaceitKey")}</p>}
      {leetifyFailed && <p className="banner banner-warning">{t("leaderboard.leetifyDown")}</p>}

      {source === "aim" && <AimCard board={data} />}

      {source === "aim" ? null : isLeetify && leetify.isPending ? (
        <div className="card">
          <Loading />
        </div>
      ) : isLeetify && leetify.error ? (
        <ErrorState message={errorText(t, leetify.error)} onRetry={() => void leetify.refetch()} />
      ) : withValue.length === 0 ? (
        <div className="card">
          <p className="muted">{t(`leaderboard.nothing.${source}`)}</p>
        </div>
      ) : (
        <div className="lb-top">
          <ol className="podium" aria-label={t("leaderboard.podium", { column: col.label })}>
            {podium.map((m, i) => (
              <li key={m.id} className={`podium-step place-${i + 1}${m.me ? " is-me" : ""}`}>
                <span className="podium-rank">{i + 1}</span>
                <Avatar name={m.name} url={m.avatarUrl} size={i === 0 ? 64 : 52} />
                <span className="podium-name">{m.name}</span>
                {/* Leetify's numbers come as they are, long ones too: the text shrinks rather than overflowing. */}
                <span className={`podium-value${fmt(col, col.value(m.id)).length > 6 ? " is-long" : ""}`}>{fmt(col, col.value(m.id))}</span>
                <span className="muted small">{col.label}</span>
              </li>
            ))}
          </ol>
          {others.length > 0 && (
            <ol className="lb-next" start={4}>
              {others.map((m, i) => (
                <li key={m.id} className={m.me ? "is-me" : undefined}>
                  <span className="lb-next-rank">{col.value(m.id) === null ? "–" : podium.length + i + 1}</span>
                  <Avatar name={m.name} url={m.avatarUrl} size={28} />
                  <span>{m.name}</span>
                  <b className="lb-next-value">{fmt(col, col.value(m.id))}</b>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <section className="card lb-table-card">
        <div className="lb-scroll">
          <table className="lb-table">
            <thead>
              <tr>
                <th scope="col">{t("leaderboard.player")}</th>
                {cols.map((c) => (
                  <th key={c.key} scope="col" aria-sort={c === col ? (sort.dir === "desc" ? "descending" : "ascending") : undefined}>
                    <button type="button" className="lb-sort" onClick={() => setSort({ key: c.key, dir: c === col && sort.dir === "desc" ? "asc" : "desc" })}>
                      {c.label}
                      {c === col && <span aria-hidden="true">{sort.dir === "desc" ? " ↓" : " ↑"}</span>}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => {
                const url = profile(m);
                return (
                  <tr key={m.id} className={m.me ? "is-me" : undefined}>
                    <th scope="row">
                      <span className="lb-player">
                        <Avatar name={m.name} url={m.avatarUrl} size={26} />
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer">
                            {m.name}
                          </a>
                        ) : (
                          m.name
                        )}
                        {m.me && <span className="muted small">{t("live.you")}</span>}
                        {leetifyUrl(m) && <ViewOnLeetify url={leetifyUrl(m)!} />}
                        {m.hidden && <span className="lb-hidden-tag">{t("leaderboard.hiddenTag")}</span>}
                      </span>
                    </th>
                    {cols.map((c) => {
                      const v = c.value(m.id);
                      return (
                        <td key={c.key} className={v !== null && v === best[c.key] ? "is-best" : undefined}>
                          {fmt(c, v)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="lb-foot">
          <p className="muted small">{t(`leaderboard.foot.${source}`)}</p>
          {isLeetify && <LeetifyCredit />}
        </div>
      </section>
    </div>
  );
}

/** My settings, then everyone's eDPI on one scale with the team's average. */
function AimCard({ board }: { board: Leaderboard }) {
  const { t } = useTranslation();
  const me = board.members.find((m) => m.me);
  const rows = board.members.map((m) => ({ m, e: edpi(board.aim[m.id]) }));
  const known = rows.filter((r): r is { m: LeaderboardMember; e: number } => r.e !== null).sort((a, b) => b.e - a.e);
  const max = Math.max(...known.map((r) => r.e), 1);
  const avg = known.length ? known.reduce((n, r) => n + r.e, 0) / known.length : null;
  const missing = rows.filter((r) => r.e === null).map((r) => r.m);
  return (
    <div className="aim-grid is-compare-only">
      <section className="card aim-compare">
        <div className="row aim-compare-head">
          <h2 className="panel-title">{t("aim.compareTitle")}</h2>
          {me && (
            <Link to="/settings/account#aim" className="btn btn-secondary btn-small">
              {board.aim[me.id]?.dpi ? t("aim.changeMine") : t("aim.enterMine")}
            </Link>
          )}
        </div>
        {known.length === 0 ? (
          <p className="muted">{t("aim.nobody")}</p>
        ) : (
          <>
            <ol className="aim-bars" aria-label={t("aim.compareTitle")}>
              {known.map(({ m, e }) => (
                <li key={m.id} className={m.me ? "is-me" : undefined}>
                  <span className="aim-name">
                    <Avatar name={m.name} url={m.avatarUrl} size={22} />
                    {m.name}
                  </span>
                  <span className="aim-track">
                    <span className="aim-bar" style={{ width: `${(e / max) * 100}%` }} />
                    {avg !== null && known.length > 1 && <span className="aim-avg" style={{ left: `${(avg / max) * 100}%` }} aria-hidden="true" />}
                  </span>
                  <span className="aim-value">
                    <b>{plain(e)}</b>
                    <span className="muted small">{t("aim.cmShort", { cm: cmPer360(e)!.toFixed(1) })}</span>
                  </span>
                </li>
              ))}
            </ol>
            {avg !== null && known.length > 1 && (
              <p className="muted small aim-legend">
                <span className="aim-avg-key" aria-hidden="true" />
                {t("aim.average", { edpi: Math.round(avg), cm: cmPer360(avg)!.toFixed(1) })}
              </p>
            )}
          </>
        )}
        {missing.length > 0 && <p className="muted small">{t("aim.missing", { names: missing.map((m) => m.name).join(", ") })}</p>}
      </section>
    </div>
  );
}
